/**
 * @file useForm
 * @author sunwen05
 */

import _ from 'lodash';
import {useState, useImperativeHandle, useEffect, useMemo} from 'react';
import {useOriginalCopy} from '@huse/previous-value';
import {Form} from 'acud';

import {defaultOptions} from '../../../components/biz/HooksForm/constants';
import {isValue} from '../../../utils/bizUtil';
import useFetchData from './fetchData';
import useSubmit from './submit';
import useStepForm from './stepForm';
import {
    UseFormOptions,
    FetchDataAction,
    SubmitAction,
    FetchOptionsAction,
    StepControls,
    DuFormItemControl
} from './interface';

export default (ref: any, props: any, options: UseFormOptions) => {
    // 透传
    const {actions, onSuccess} = props;
    // options
    const {
        type,
        controls,
        actionConfig = {},
        customFunctions = {},
        formItemOption = {},
        stepConfig = {}
    } = options;
    // action配置
    const {
        fetchDataAction,
        fetchOptionsAction,
        submitAction
    } = actionConfig;
    // 获取form
    const [form] = Form.useForm();

    // useSubmit -> 获取初次渲染数据和
    const {fetchData, optionsData} = useFetchData(
        actions,
        fetchOptionsAction as FetchOptionsAction[],
        fetchDataAction as FetchDataAction
    );

    // useState -> 存储渲染数据
    const [formData, setFormData] = useState(fetchData);

    // useSubmit -> 获取提交方法及提交状态
    const {isSubmiting, setSubmiting, doSubmit, actualSubmit} = useSubmit(
        form,
        actions,
        submitAction as SubmitAction,
        fetchData,
        customFunctions,
        onSuccess
    );
    // 关联doSubmit到ref上
    useImperativeHandle(ref, () => ({doSubmit: type === 'step' ? doStepSubmit : doSubmit}));

    // render所需要的参数
    const formItemOptionMerge = {...defaultOptions, ...formItemOption, isSubmiting};

    // 渲染controls
    const controlsConfig = controls(props, optionsData, customFunctions, formData);

    const defaultValues = useMemo(() => (
        _.assign({}, ...(
            _.map(controlsConfig, control => {
                if (type === 'step') {
                    const {controls} = control as StepControls;
                    const values = _.map(controls, item => {
                        const {defaultValue, name} = item as DuFormItemControl;
                        if (isValue(defaultValue) && name) {
                            return {[name]: defaultValue};
                        }
                    });
                    return _.assign({}, ...values);
                }
                const {defaultValue, name} = control as DuFormItemControl;
                if (isValue(defaultValue) && name) {
                    return {[name]: defaultValue};
                }
            })
        ))
    ), [controlsConfig, type]);

    // useStepForm -> 当为分步表单时即type=step，获取到当前的stepIndex、buttonConfig、doStepSubmit
    const {
        stepIndex,
        buttonConfig: stepButtonConfig,
        doStepSubmit
    } = useStepForm(
        form,
        controlsConfig as StepControls[],
        stepConfig,
        actualSubmit,
        formData,
        setFormData,
        setSubmiting
    );

    const originalFetchData = useOriginalCopy(fetchData);
    useEffect(() => {
        setFormData((prevFormData: object) => ({
            ...prevFormData,
            ...originalFetchData
        }));
    }, [originalFetchData]);

    return {
        form,
        formRef: {current: form}, // 适配老的代码formRef
        formProps: {
            // controls
            controlsConfig,
            // fetchData
            optionsData,
            formData: {...defaultValues, ...formData},
            // submit
            doSubmit: type === 'step' ? doStepSubmit : doSubmit,
            isSubmiting,
            setSubmiting,
            // step
            type,
            stepIndex,
            stepButtonConfig,
            // options
            formItemOptionMerge,
            form
        }
    };
};
